import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Logo } from "../../components/Logo";
import { ArrowLeft, Mail, Trash2, Check, Inbox } from "lucide-react";

export default function AdminMessages() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | unread | read
  const [selected, setSelected] = useState(null);

  useEffect(() => { if (user === false) navigate("/admin/login"); }, [user, navigate]);

  const load = () => {
    setLoading(true);
    api.get("/admin/contact").then((r) => setMessages(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { if (user && (user.role === "admin" || user.role === "editor")) load(); }, [user]);

  const markRead = async (id) => {
    await api.put(`/admin/contact/${id}/read`);
    setMessages((m) => m.map((x) => (x.id === id ? { ...x, is_read: true } : x)));
    if (selected?.id === id) setSelected({ ...selected, is_read: true });
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer ce message ?")) return;
    await api.delete(`/admin/contact/${id}`);
    setMessages((m) => m.filter((x) => x.id !== id));
    if (selected?.id === id) setSelected(null);
  };

  const filtered = messages.filter((m) =>
    filter === "all" ? true : filter === "unread" ? !m.is_read : m.is_read
  );
  const unreadCount = messages.filter((m) => !m.is_read).length;

  if (!user || user === null) return <div className="py-32 text-center font-mono-label">CHARGEMENT…</div>;

  return (
    <div className="min-h-screen bg-[#FAF8F3]" data-testid="admin-messages">
      <header className="border-b-2 border-[#1A1A18] bg-[#FAF8F3] sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="font-mono-label flex items-center gap-1 hover:text-[#4A7A3D]" data-testid="messages-back"><ArrowLeft size={14} /> Retour</Link>
            <Logo size="sm" />
            <span className="font-mono-label text-[#4A7A3D] hidden md:inline">MESSAGES</span>
          </div>
          {unreadCount > 0 && (
            <span className="font-mono-label bg-[#C4944A] text-[#1A1A18] px-3 py-1.5">{unreadCount} non lu{unreadCount > 1 ? "s" : ""}</span>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="font-mono-label text-[#4A7A3D] mb-2 flex items-center gap-2"><Mail size={14} /> Boîte de réception</div>
        <h1 className="font-display text-4xl md:text-6xl leading-[1.0] mb-8">Messages</h1>

        <div className="flex gap-2 mb-6">
          {[{ v: "all", l: `Tous (${messages.length})` }, { v: "unread", l: `Non lus (${unreadCount})` }, { v: "read", l: "Lus" }].map((f) => (
            <button key={f.v} onClick={() => setFilter(f.v)}
              className={`font-mono-label px-4 py-2 border-2 ${filter === f.v ? "bg-[#1A1A18] text-[#FAF8F3] border-[#1A1A18]" : "border-[#E0D8CE] hover:border-[#1A1A18]"}`}
              data-testid={`messages-filter-${f.v}`}>
              {f.l}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="font-mono-label">CHARGEMENT…</p>
        ) : filtered.length === 0 ? (
          <div className="py-20 text-center border-2 border-dashed border-[#E0D8CE]">
            <Inbox size={48} className="mx-auto text-[#E0D8CE]" />
            <p className="font-quote text-2xl text-[#4B4B48] mt-4">Aucun message {filter !== "all" ? `(${filter})` : ""}.</p>
          </div>
        ) : (
          <div className="grid lg:grid-cols-12 gap-6">
            <div className="lg:col-span-5 border-2 border-[#1A1A18] divide-y divide-[#E0D8CE] max-h-[70vh] overflow-y-auto">
              {filtered.map((m) => (
                <button key={m.id} onClick={() => { setSelected(m); if (!m.is_read) markRead(m.id); }}
                  className={`w-full text-left p-4 hover:bg-[#F5EFEB] block ${selected?.id === m.id ? "bg-[#F5EFEB]" : ""} ${!m.is_read ? "border-l-4 border-[#C4944A]" : ""}`}
                  data-testid={`message-row-${m.id}`}>
                  <div className="flex justify-between items-start gap-2 mb-1">
                    <div className="font-display text-lg leading-tight truncate">{m.name}</div>
                    <div className="font-mono-label text-[#4B4B48] text-[10px] flex-shrink-0">{new Date(m.received_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}</div>
                  </div>
                  <div className="font-mono-label text-[#4A7A3D] truncate">{m.subject || "(sans sujet)"}</div>
                  <div className="text-sm text-[#4B4B48] truncate mt-1">{m.message.slice(0, 80)}…</div>
                </button>
              ))}
            </div>

            <div className="lg:col-span-7">
              {!selected ? (
                <div className="py-16 text-center border-2 border-dashed border-[#E0D8CE]">
                  <p className="font-quote text-xl text-[#4B4B48]">Sélectionne un message à gauche.</p>
                </div>
              ) : (
                <article className="border-2 border-[#1A1A18] p-6 md:p-8" data-testid="message-detail">
                  <div className="flex justify-between items-start gap-4 mb-4">
                    <div>
                      <div className="font-mono-label text-[#4A7A3D]">{new Date(selected.received_at).toLocaleString("fr-FR")}</div>
                      <h2 className="font-display text-3xl md:text-4xl leading-tight mt-2">{selected.subject || "(sans sujet)"}</h2>
                    </div>
                    <button onClick={() => del(selected.id)} className="border-2 border-red-700 text-red-700 p-2 hover:bg-red-700 hover:text-white" data-testid="message-delete">
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <div className="grid md:grid-cols-2 gap-3 text-sm bg-[#F5EFEB] p-4 mb-6">
                    <div><span className="font-mono-label text-[#4B4B48]">DE :</span> <span className="font-display text-base">{selected.name}</span></div>
                    <div><span className="font-mono-label text-[#4B4B48]">EMAIL :</span> <a href={`mailto:${selected.email}`} className="text-[#4A7A3D] underline">{selected.email}</a></div>
                  </div>
                  <p className="prose-rwana whitespace-pre-wrap">{selected.message}</p>
                  <div className="mt-8 pt-6 border-t-2 border-[#1A1A18] flex flex-wrap gap-3">
                    <a href={`mailto:${selected.email}?subject=Re: ${encodeURIComponent(selected.subject || "Votre message")}`}
                      className="bg-[#4A7A3D] text-[#FAF8F3] px-6 py-3 font-mono-label hover:bg-[#3E6633]" data-testid="message-reply">
                      Répondre par email
                    </a>
                    {!selected.is_read && (
                      <button onClick={() => markRead(selected.id)} className="border-2 border-[#1A1A18] px-6 py-3 font-mono-label hover:bg-[#1A1A18] hover:text-[#FAF8F3] flex items-center gap-2" data-testid="message-mark-read">
                        <Check size={14} /> Marquer comme lu
                      </button>
                    )}
                  </div>
                </article>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
